import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { Heading, Text, Button, Input, LoadingSpinner } from '../components/ui'
import { BRAND_COLOR } from '../constants'
import { supabase } from '../lib/supabase'
import { 
  listItems,
  createItem,
  updateItem,
  deleteItem
} from '../services/informativo'
import { InformativoItem, CreateInformativoPayload } from '../types/informativo'
import { modulesService } from '../services/modules'
import { Module } from '../types'
import PdfModal from '../components/PdfModal'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon,
  DocumentTextIcon,
  DocumentArrowUpIcon,
  XCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

const Circulando: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'versoes' | 'gerenciar'>('versoes')
  
  // Hooks de permissões
  const { hasPermission: canInsert } = usePermission('informativo_circulando', 'insert')
  const { hasPermission: canUpdate } = usePermission('informativo_circulando', 'update')
  const { hasPermission: canDelete } = usePermission('informativo_circulando', 'delete')
  
  // Estados principais
  const [items, setItems] = useState<InformativoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [module, setModule] = useState<Module | null>(null)

  // Estados de modais (para aba Gerenciar)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<InformativoItem | null>(null)

  // Estados para modal de PDF
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfTitle, setPdfTitle] = useState<string>('Visualizar PDF')

  // Estados de formulário (para aba Gerenciar)
  const [formData, setFormData] = useState<CreateInformativoPayload>({
    date: '',
    title: '',
    description: '',
    file: null,
    link: null,
    type: 'circulando'
  })
  const [modalLoading, setModalLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [currentFilePath, setCurrentFilePath] = useState<string>('')

  // Estados de filtros e busca (para aba Versões)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  const [selectedDate, setSelectedDate] = useState(searchParams.get('data') || '')

  // Estados de paginação (para aba Versões)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  // Função para buscar dados
  const fetchData = async (opts?: { showOverlay?: boolean }) => {
    try {
      const showOverlay = opts?.showOverlay ?? true
      if (showOverlay) {
        setLoading(true)
      }
      setError(null)

      const itemsData = await listItems('circulando')
      setItems(itemsData)
      setRetryCount(0)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  // Função de retry
  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  // Debounce do termo de busca
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Resetar página para 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, selectedDate])

  // Carregar dados iniciais e aplicar filtro da URL
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      const dataParam = searchParams.get('data')
      if (dataParam) {
        setSelectedDate(dataParam)
      }
      fetchData({ showOverlay: true })
    }
  }, [searchParams])

  // Carregar informações do módulo
  useEffect(() => {
    const loadModule = async () => {
      const { data } = await modulesService.getByName('informativo_circulando')
      if (data) setModule(data)
    }
    loadModule()
  }, [])

  // Funções de modal (para aba Gerenciar)
  const openModal = (item?: InformativoItem) => {
    // Verificar permissões
    if (item) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar itens.')
        return
      }
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar itens.')
        return
      }
    }

    if (item) {
      setEditingItem(item)
      setFormData({
        date: item.date,
        title: item.title,
        description: item.description,
        file: item.file,
        link: item.link,
        type: item.type
      })
      setCurrentFilePath(item.file || '')
    } else {
      setEditingItem(null)
      setFormData({
        date: '',
        title: '',
        description: '',
        file: null,
        link: null,
        type: 'circulando'
      })
      setCurrentFilePath('')
    }
    setSelectedFile(null)
    setValidationErrors({})
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingItem(null)
    setValidationErrors({})
    setFormData({
      date: '',
      title: '',
      description: '',
      file: null,
      link: null,
      type: 'circulando'
    })
    setSelectedFile(null)
    setCurrentFilePath('')
  }

  // Função de validação
  const validateForm = () => {
    const errors: Record<string, string> = {}
    
    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Título é obrigatório'
    }
    
    if (!formData.date || formData.date === '') {
      errors.date = 'Data é obrigatória'
    }
    
    if (!formData.description || formData.description.trim() === '') {
      errors.description = 'Descrição é obrigatória'
    }
    
    // Para circulando, pelo menos um dos campos (file ou link) deve ser preenchido
    if (formData.type === 'circulando' && !formData.file && !formData.link) {
      errors.file = 'Arquivo ou link é obrigatório'
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Função de upload de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      alert('Por favor, selecione apenas arquivos PDF.')
      event.target.value = ''
      return
    }

    const maxSize = 50 * 1024 * 1024 // 50MB
    if (file.size > maxSize) {
      alert('O arquivo é muito grande. Tamanho máximo: 50MB.')
      event.target.value = ''
      return
    }

    setSelectedFile(file)
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setCurrentFilePath('')
    setFormData(prev => ({ ...prev, file: null }))
  }

  const handleUploadFile = async () => {
    if (!selectedFile) return

    setIsUploadingFile(true)

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const fileName = `circulando_${timestamp}.pdf`
      const filePath = fileName

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('circulando')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/pdf'
        })

      if (uploadError) {
        let errorMessage = `Erro no upload: ${uploadError.message}`
        
        if (uploadError.message.includes('Bucket not found')) {
          errorMessage = 'O bucket "circulando" não foi encontrado. Por favor, crie o bucket no Supabase Dashboard > Storage.'
        } else if (uploadError.message.includes('new row violates row-level security policy')) {
          errorMessage = 'Erro de permissão. Verifique as políticas de acesso do bucket "circulando" no Supabase.'
        } else if (uploadError.message.includes('The resource already exists')) {
          errorMessage = 'Um arquivo com este nome já existe. Tente novamente.'
        }
        
        throw new Error(errorMessage)
      }

      if (!uploadData) {
        throw new Error('Upload concluído mas nenhum dado foi retornado.')
      }

      if (editingItem && currentFilePath && currentFilePath !== filePath) {
        try {
          await supabase.storage
            .from('circulando')
            .remove([currentFilePath])
        } catch (err) {
          // Não falhar o upload por causa da remoção do arquivo antigo
        }
      }

      setFormData(prev => ({ ...prev, file: filePath }))
      setCurrentFilePath(filePath)
      setSelectedFile(null)
      
      alert('Arquivo enviado com sucesso!')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao fazer upload do arquivo'
      alert(errorMessage)
    } finally {
      setIsUploadingFile(false)
    }
  }

  // Função para obter URL pública do arquivo
  const getFileUrl = (filePath: string | null) => {
    if (!filePath) return null
    const { data } = supabase.storage
      .from('circulando')
      .getPublicUrl(filePath)
    return data.publicUrl
  }

  // Função para normalizar URL (adicionar protocolo se necessário)
  const normalizeUrl = (url: string): string => {
    if (!url) return url
    // Se já tem protocolo, retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Se não tem protocolo, adicionar https://
    return `https://${url}`
  }

  // Funções de CRUD
  const handleSave = async () => {
    if (!validateForm()) {
      return
    }
    
    try {
      setModalLoading(true)
      
      if (selectedFile && !formData.file) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const fileName = `circulando_${timestamp}.pdf`
        const filePath = fileName

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('circulando')
          .upload(filePath, selectedFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: 'application/pdf'
          })

        if (uploadError) {
          let errorMessage = `Erro no upload: ${uploadError.message}`
          
          if (uploadError.message.includes('Bucket not found')) {
            errorMessage = 'O bucket "circulando" não foi encontrado. Por favor, crie o bucket no Supabase Dashboard > Storage.'
          } else if (uploadError.message.includes('new row violates row-level security policy')) {
            errorMessage = 'Erro de permissão. Verifique as políticas de acesso do bucket "circulando" no Supabase.'
          } else if (uploadError.message.includes('The resource already exists')) {
            errorMessage = 'Um arquivo com este nome já existe. Tente novamente.'
          }
          
          throw new Error(errorMessage)
        }

        if (!uploadData) {
          throw new Error('Upload concluído mas nenhum dado foi retornado.')
        }

        if (editingItem && currentFilePath && currentFilePath !== filePath) {
          try {
            await supabase.storage
              .from('circulando')
              .remove([currentFilePath])
          } catch (err) {
            // Não falhar o upload por causa da remoção do arquivo antigo
          }
        }

        const updatedFormData = { ...formData, file: uploadData.path }
        setFormData(updatedFormData)
        setCurrentFilePath(uploadData.path)
        setSelectedFile(null)
        
        if (editingItem) {
          await updateItem(editingItem.id, updatedFormData)
        } else {
          await createItem(updatedFormData)
        }
      } else {
        if (editingItem) {
          await updateItem(editingItem.id, formData)
        } else {
          await createItem(formData)
        }
      }
      
      await fetchData()
      closeModal()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao salvar item')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir itens.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      try {
        const itemToDelete = items.find(item => item.id === id)
        
        await deleteItem(id)
        
        if (itemToDelete?.file) {
          try {
            await supabase.storage
              .from('circulando')
              .remove([itemToDelete.file])
          } catch (err) {
            console.warn('Erro ao remover arquivo do storage:', err)
          }
        }
        
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir item:', err)
        alert(err instanceof Error ? err.message : 'Erro ao excluir item')
      }
    }
  }

  // Limpar filtros (para aba Versões)
  const handleClearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedDate('')
    setCurrentPage(1)
  }

  // Filtrar itens (para aba Versões)
  const filteredItems = items.filter(item => {
    const matchesSearch = !debouncedSearchTerm || 
      item.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    
    const matchesDate = !selectedDate || item.date === selectedDate
    
    return matchesSearch && matchesDate
  })

  // Calcular valores de paginação
  const totalItems = filteredItems.length
  const pagesCount = Math.ceil(totalItems / itemsPerPage)
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  
  const showingStart = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const showingEnd = Math.min(currentPage * itemsPerPage, totalItems)

  const hasFilters = debouncedSearchTerm || selectedDate

  // Ajustar página atual se ela for maior que o número total de páginas
  useEffect(() => {
    if (pagesCount > 0 && currentPage > pagesCount) {
      setCurrentPage(1)
    }
  }, [pagesCount, currentPage])

  // Funções de paginação
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagesCount) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="2xl" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Circulando...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="2xl" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Circulando
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : (
          <PermissionGuard 
            moduleName="informativo_circulando"
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="2xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar o Circulando.
                  </Text>
                </div>
              </div>
            }
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {module?.display_name || 'Circulando'}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-2">
                    {activeTab === 'versoes' 
                      ? `${totalItems} ${totalItems === 1 ? 'artigo' : 'artigos'}`
                      : 'Gerencie os artigos do Circulando'
                    }
                  </Text>
                </div>
                {activeTab === 'gerenciar' && (
                  <Button
                    onClick={() => openModal()}
                    className="flex items-center gap-2"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    <PlusIcon className="h-5 w-5" />
                    Novo Item
                  </Button>
                )}
              </div>
            </div>

            {/* Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('versoes')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'versoes'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Versões
                </button>
                <button
                  onClick={() => setActiveTab('gerenciar')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'gerenciar'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Gerenciar
                </button>
              </div>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'versoes' ? (
              <>
                {/* Filtros */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Text size="sm" className="mb-2">Buscar</Text>
                      <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Buscar por título ou descrição..."
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Text size="sm" className="mb-2">Data</Text>
                      <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>

                    <div className="flex items-end">
                      {hasFilters && (
                        <Button
                          onClick={handleClearFilters}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Limpar filtros
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Lista de Artigos */}
                <div className="flex-1 overflow-y-auto p-6">
                  {paginatedItems.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <Text size="lg" className="text-gray-500 mb-2">
                        Nenhum artigo encontrado
                      </Text>
                      <Text size="sm" className="text-gray-400">
                        {hasFilters ? 'Tente ajustar os filtros' : 'Não há artigos cadastrados'}
                      </Text>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {paginatedItems.map((item) => (
                        <div 
                          key={item.id} 
                          onClick={() => {
                            if (item.file && getFileUrl(item.file)) {
                              // Abrir PDF em modal
                              setPdfUrl(getFileUrl(item.file)!)
                              setPdfTitle(item.title)
                              setShowPdfModal(true)
                            } else if (item.link) {
                              // Links abrem em nova aba
                              const normalizedLink = normalizeUrl(item.link)
                              window.open(normalizedLink, '_blank')
                            }
                          }}
                          className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-shadow ${
                            (item.file && getFileUrl(item.file)) || item.link
                              ? 'hover:shadow-md cursor-pointer' 
                              : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                  {item.title}
                                </Heading>
                              </div>
                              
                              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="h-4 w-4" />
                                  {new Date(item.date).toLocaleDateString('pt-BR')}
                                </div>
                                {item.file && (
                                  <div className="flex items-center gap-1">
                                    <DocumentTextIcon className="h-4 w-4" />
                                    {item.file}
                                  </div>
                                )}
                                {item.link && (
                                  <div className="flex items-center gap-1">
                                    <DocumentTextIcon className="h-4 w-4" />
                                    Link
                                  </div>
                                )}
                              </div>
                              
                              <div className="text-gray-600 dark:text-gray-300 line-clamp-3">
                                {item.description}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Paginação */}
                {!loading && totalItems > 0 && (
                  <div className="mt-6 w-full px-6 pb-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-center gap-4">
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          Mostrando {showingStart} a {showingEnd} de {totalItems} resultados
                        </Text>
                        <select
                          value={itemsPerPage.toString()}
                          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          por página
                        </Text>
                      </div>
                      
                      {pagesCount > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            size="sm"
                            variant="outline"
                          >
                            Anterior
                          </Button>
                          
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagesCount) }, (_, i) => {
                              const page = i + 1
                              return (
                                <Button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  size="sm"
                                  variant={currentPage === page ? "solid" : "outline"}
                                  className={currentPage === page ? "bg-blue-600 text-white" : ""}
                                >
                                  {page}
                                </Button>
                              )
                            })}
                            {pagesCount > 5 && (
                              <>
                                <Text size="sm" className="text-gray-500">...</Text>
                                <Button
                                  onClick={() => handlePageChange(pagesCount)}
                                  size="sm"
                                  variant={currentPage === pagesCount ? "solid" : "outline"}
                                  className={currentPage === pagesCount ? "bg-blue-600 text-white" : ""}
                                >
                                  {pagesCount}
                                </Button>
                              </>
                            )}
                          </div>
                          
                          <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagesCount}
                            size="sm"
                            variant="outline"
                          >
                            Próxima
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Lista de Itens - Gerenciar */}
                <div className="flex-1 overflow-y-auto p-6">
                  {items.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <Text size="lg" className="text-gray-500 mb-2">
                        Nenhum item encontrado
                      </Text>
                      <Text size="sm" className="text-gray-400">
                        Clique em "Novo Item" para criar o primeiro
                      </Text>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Título
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Data
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Arquivo/Link
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {item.title}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {new Date(item.date).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {item.file ? (
                                  <a
                                    href={getFileUrl(item.file)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                  >
                                    <DocumentTextIcon className="h-4 w-4" />
                                    {item.file}
                                  </a>
                                ) : item.link ? (
                                  <a
                                    href={normalizeUrl(item.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                  >
                                    <DocumentTextIcon className="h-4 w-4" />
                                    Link
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <div title="Editar">
                                    <Button
                                      onClick={() => openModal(item)}
                                      size="sm"
                                      variant="outline"
                                      className="flex items-center justify-center"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div title="Excluir">
                                    <Button
                                      onClick={() => handleDelete(item.id)}
                                      size="sm"
                                      variant="outline"
                                      className="flex items-center justify-center text-red-600 hover:text-red-700"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </PermissionGuard>
        )}
      </div>

      {/* Modal de Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingItem ? 'Editar Item' : 'Novo Item'}
              </Heading>
              <Button
                onClick={closeModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Título */}
                <div>
                  <Text size="sm" className="mb-2">
                    Título <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Digite o título..."
                    className={validationErrors.title ? 'border-red-500' : ''}
                  />
                  {validationErrors.title && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.title}
                    </Text>
                  )}
                </div>

                {/* Data */}
                <div>
                  <Text size="sm" className="mb-2">
                    Data <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className={validationErrors.date ? 'border-red-500' : ''}
                  />
                  {validationErrors.date && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.date}
                    </Text>
                  )}
                </div>

                {/* Arquivo */}
                <div>
                  <Text size="sm" className="mb-2">
                    Arquivo PDF
                  </Text>
                  
                  {/* Arquivo atual */}
                  {currentFilePath && !selectedFile && (
                    <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {currentFilePath}
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        {getFileUrl(currentFilePath) && (
                          <a
                            href={getFileUrl(currentFilePath)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            Baixar
                          </a>
                        )}
                        <Button
                          onClick={handleRemoveFile}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Arquivo selecionado para upload */}
                  {selectedFile && (
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedFile.name}
                        </Text>
                        <Text size="xs" className="text-gray-500 dark:text-gray-400">
                          ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={handleUploadFile}
                          size="sm"
                          disabled={isUploadingFile}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {isUploadingFile ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                              Enviar
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedFile(null)
                            const fileInput = document.getElementById('file-input') as HTMLInputElement
                            if (fileInput) fileInput.value = ''
                          }}
                          size="sm"
                          variant="outline"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Input de arquivo */}
                  {!selectedFile && !currentFilePath && (
                    <div>
                      <label
                        htmlFor="file-input"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <DocumentArrowUpIcon className="w-10 h-10 mb-2 text-gray-400" />
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            <span className="font-semibold">Clique para selecionar</span> ou arraste o arquivo PDF
                          </Text>
                          <Text size="xs" className="text-gray-400 dark:text-gray-500 mt-1">
                            Apenas arquivos PDF (máx. 50MB)
                          </Text>
                        </div>
                        <input
                          id="file-input"
                          type="file"
                          accept="application/pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}

                  {/* Botão para adicionar novo arquivo quando já existe um */}
                  {currentFilePath && !selectedFile && (
                    <div className="mt-2">
                      <label
                        htmlFor="file-input-replace"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm"
                      >
                        <DocumentArrowUpIcon className="h-4 w-4" />
                        Substituir arquivo
                      </label>
                      <input
                        id="file-input-replace"
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>

                {/* Link */}
                <div>
                  <Text size="sm" className="mb-2">
                    Link (opcional)
                  </Text>
                  <Input
                    type="url"
                    value={formData.link || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value || null }))}
                    placeholder="https://exemplo.com/noticia"
                    className={validationErrors.link ? 'border-red-500' : ''}
                  />
                  {validationErrors.link && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.link}
                    </Text>
                  )}
                  <Text size="xs" className="text-gray-500 dark:text-gray-400 mt-1">
                    Você pode informar um link em vez de ou além do arquivo PDF
                  </Text>
                </div>

                {/* Descrição */}
                <div>
                  <Text size="sm" className="mb-2">
                    Descrição <span className="text-red-500">*</span>
                  </Text>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Digite a descrição..."
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${
                      validationErrors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    rows={5}
                  />
                  {validationErrors.description && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.description}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={modalLoading || !formData.title.trim() || !formData.description.trim() || !formData.date || (!formData.file && !formData.link)}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingItem ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de PDF */}
      <PdfModal
        isOpen={showPdfModal}
        onClose={() => {
          setShowPdfModal(false)
          setPdfUrl(null)
          setPdfTitle('Visualizar PDF')
        }}
        pdfUrl={pdfUrl}
        title={pdfTitle}
      />
    </div>
  )
}

export default Circulando





